/* SearchLogic.tsx
 * This module provides the search functionality for grid components in our application.
 * It implements a sophisticated ranking system for search results across different data types,
 * supporting partial matches, case sensitivity, and custom value mappings.
 *
 * The search algorithm:
 * 1. Breaks search input into keywords
 * 2. Scores each data item against all keywords
 * 3. Only returns results where all keywords match somewhere
 * 4. Ranks results by total score
 */

// TypeScript interfaces
interface RankingScores {
  EXACT_MATCH: number
  WORD_START: number
  SUBSTRING_MATCH: number
  CASE_MATCH: number
  CONSECUTIVE_CHAR: number
  LENGTH_FACTOR: number
  EXACT_CASE_CHAR: number
}

interface Header {
  key: string
  type: 'string' | 'datetime' | 'boolean' | 'number'
  isSearchable?: boolean
  valueMapping?: { [key: string]: string } | { [key: number]: string }
  searchRankingRule?: (keyword: string, value: any) => number
}

interface DataItem {
  [key: string]: any
  _searchId: string
  _originalIndex?: number
}

interface SearchResult {
  results: string[]
  scores: Map<string, number>
}

type SearchHandler = (value: any, searchTerm: string, header?: Header) => number

/* Scoring weights for different match types
 * Higher weights indicate more significant matches
 */
const RANKING_SCORES: RankingScores = {
  EXACT_MATCH: 100, // Complete string match
  WORD_START: 25, // Word boundary match (e.g., "tech" matches "technology")
  SUBSTRING_MATCH: 15, // Match anywhere in string
  CASE_MATCH: 10, // Correct case matching
  CONSECUTIVE_CHAR: 0.3, // Bonus for each consecutive matching character
  LENGTH_FACTOR: 0.2, // Bonus multiplier for match length
  EXACT_CASE_CHAR: 0.1, // Small bonus for individual character case matches
}

/* Utility function to stringify complex values for search
 * Handles arrays, objects, and primitive types
 */
export const stringifyForSearch = (value: any): string => {
  if (value == null) return ''

  // Handle arrays (like lists of objects)
  if (Array.isArray(value)) {
    // If array elements have a 'name' property, use that
    if (value.length > 0 && typeof value[0] === 'object' && value[0]?.name) {
      return value.map((item) => item.name).join(', ')
    }

    // Otherwise just join the array values
    return value.join(', ')
  }

  // Handle objects by checking for common name properties
  if (typeof value === 'object') {
    if (value.name) return String(value.name)
    if (value.title) return String(value.title)
    if (value.label) return String(value.label)

    // If no common property found, try to convert the whole object
    try {
      const str = JSON.stringify(value)

      return str === '{}' ? '' : str
    } catch {
      return ''
    }
  }

  // Handle primitive types
  return String(value)
}

/* String matching algorithm with weighted scoring
 * Returns a normalized score based on match quality
 */
const calculateStringMatchScore = (source: any, searchTerm: string): number => {
  if (!source || !searchTerm) return 0

  const sourceStr = String(source).trim()
  const searchStr = String(searchTerm).trim()

  const sourceLower = sourceStr.toLowerCase()
  const searchLower = searchStr.toLowerCase()

  // First check if there's any match at all (case-insensitive)
  if (!sourceLower.includes(searchLower)) {
    return 0
  }

  let score = 0

  // 1. Exact string match (highest priority)
  if (sourceStr === searchStr) {
    score += RANKING_SCORES.EXACT_MATCH
  }

  // 2. Find best matching position and calculate consecutive bonuses
  const position = sourceLower.indexOf(searchLower)

  if (position !== -1) {
    // Word boundary detection
    const isWordStart =
      position === 0 || !/[a-z0-9]/i.test(sourceStr[position - 1])

    // Position penalty (higher score for matches closer to start)
    const positionPenalty = 1 - position / sourceStr.length

    // Length ratio (longer matches get higher scores)
    const lengthRatio = searchStr.length / sourceStr.length

    // Add position-based score
    if (isWordStart) {
      score += RANKING_SCORES.WORD_START * positionPenalty
    }

    // Add substring match score with position and length factors
    score += RANKING_SCORES.SUBSTRING_MATCH * positionPenalty * lengthRatio

    // Add consecutive character bonus
    score += searchStr.length * RANKING_SCORES.CONSECUTIVE_CHAR

    // Add exact case matching bonus for each character
    for (let i = 0; i < searchStr.length; i++) {
      if (sourceStr[position + i] === searchStr[i]) {
        score += RANKING_SCORES.EXACT_CASE_CHAR
      }
    }

    // Length bonus for substantial matches
    if (searchStr.length > 2) {
      score += searchStr.length * RANKING_SCORES.LENGTH_FACTOR
    }
  }

  return Math.round(score * 10) / 10
}

/* Type-specific handlers for different data types
 * Each handler normalizes its type to a searchable format and returns a score
 *
 * For boolean values, uses valueMapping to convert to searchable strings
 * Example mapping: { true: 'active', false: 'inactive' }
 */
const searchHandlers: Record<string, SearchHandler> = {
  string: (value: any, searchTerm: string): number => {
    // Convert value to searchable string, handling arrays and objects
    const searchableValue = stringifyForSearch(value)

    return calculateStringMatchScore(searchableValue, searchTerm)
  },
  datetime: (value: any, searchTerm: string): number => {
    if (!value) return 0
    const date = new Date(value)
    if (isNaN(date.getTime())) return 0
    const dateStr = date.toISOString().slice(0, 10)

    return calculateStringMatchScore(dateStr, searchTerm)
  },
  boolean: (value: any, searchTerm: string, header?: Header): number => {
    if (!header?.valueMapping) return 0

    // Get the string representation from the mapping
    const mappedValue = (header.valueMapping as any)[value]

    if (!mappedValue) return 0

    // Use the standard string matching on the mapped value
    return calculateStringMatchScore(mappedValue, searchTerm)
  },
  number: (value: any, searchTerm: string): number => {
    // Try exact numeric match first
    const numSearchTerm = Number(searchTerm)

    if (!isNaN(numSearchTerm) && value === numSearchTerm)
      return RANKING_SCORES.EXACT_MATCH

    // Then try partial string matching
    const valueStr = String(value)
    const searchStr = String(searchTerm).trim()

    // If the number contains the search term as substring
    if (valueStr.includes(searchStr)) return RANKING_SCORES.SUBSTRING_MATCH

    return 0
  },
}

/* Utility to ensure each data item has a unique identifier
 * Required for stable sorting and result tracking
 */
export const ensureDataIds = (data: any[]): DataItem[] => {
  return data.map((item, index) => ({
    ...item,
    _searchId: item.id || item._searchId || `generated_id_${index}`,
  }))
}

/* Main search function
 * Handles multi-keyword search across all searchable fields
 * Requires ALL keywords to match somewhere for a result to be included
 *
 * @param data - Array of data objects to search
 * @param headers - Column definitions including search configuration
 * @param searchTerm - Space-separated search terms
 * @returns Object containing matched IDs and their scores
 */
export const performSearch = (
  data: DataItem[],
  headers: Header[],
  searchTerm: string,
): SearchResult => {
  if (!searchTerm)
    return {
      results: data.map((item) => item._searchId),
      scores: new Map(),
    }

  const keywords = searchTerm.trim().split(/\s+/)
  const scores = new Map<string, number>()

  // Initialize all scores to 0
  data.forEach((item) => scores.set(item._searchId, 0))

  // Process each data item
  data.forEach((item) => {
    let totalItemScore = 0
    let keywordMatches = new Array(keywords.length).fill(false)

    // Check each keyword
    keywords.forEach((keyword, keywordIndex) => {
      let keywordScore = 0

      // Check each searchable field for this keyword
      headers.forEach((header) => {
        if (!header.isSearchable) return

        const value = item[header.key]

        // Custom ranking rule (kept for backward compatibility)
        if (header.searchRankingRule) {
          const customScore = header.searchRankingRule(keyword, value) || 0

          keywordScore += customScore
          if (customScore > 0) keywordMatches[keywordIndex] = true

          return
        }

        // Type-specific handler with header context
        const handler = searchHandlers[header.type]

        if (handler) {
          const score = handler(value, keyword, header)

          keywordScore += score
          if (score > 0) keywordMatches[keywordIndex] = true
        }
      })

      totalItemScore += keywordScore
    })

    // Only set score if ALL keywords had at least one match
    if (keywordMatches.every((match) => match)) {
      scores.set(item._searchId, totalItemScore)
    } else {
      scores.set(item._searchId, 0)
    }
  })

  // Sort and return results
  const sortedResults = Array.from(scores.entries())
    .sort((a, b) => b[1] - a[1])
    .filter(([_, score]) => score > 0) // Remove items with zero scores

  return {
    results: sortedResults.map(([id]) => id),
    scores: scores,
  }
}

export default {
  performSearch,
  ensureDataIds,
  stringifyForSearch,
}
